import { useId, useState } from 'react';
import { RAMPA_ABC, REJILLA, SERIE, TINTA_EJE } from './paleta';

// Gráficas en SVG propio, sin librería. Son tres formas simples y el
// bundle ya pesa 700 kB: una librería de gráficas costaría más que las
// gráficas.
//
// La paleta y su validación viven en ./paleta.js.

// ---------------------------------------------------------------- marco
function Marco({ titulo, ayuda, children, tabla, filas }) {
  const [verTabla, setVerTabla] = useState(false);
  return (
    <figure className="bg-white border border-gray-200 rounded-xl p-4 m-0">
      <figcaption className="flex items-start justify-between gap-3 mb-3">
        <span>
          <span className="block text-sm font-semibold text-gray-900">{titulo}</span>
          {ayuda && <span className="block text-xs text-gray-500 mt-0.5">{ayuda}</span>}
        </span>
        {/* Toda gráfica tiene su gemela en tabla: el color nunca es la
            única vía para leer un valor. */}
        <button
          onClick={() => setVerTabla((v) => !v)}
          className="text-xs font-semibold text-[var(--color-marca)] shrink-0"
        >
          {verTabla ? 'Ver gráfica' : 'Ver tabla'}
        </button>
      </figcaption>

      {verTabla ? (
        <div className="overflow-x-auto max-h-72">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-semibold text-gray-500 uppercase border-b border-gray-200">
                {tabla.map((c) => <th key={c.etiqueta} className="px-2 py-1.5">{c.etiqueta}</th>)}
              </tr>
            </thead>
            <tbody>
              {filas.map((f, i) => (
                <tr key={i} className="border-b border-gray-100">
                  {tabla.map((c) => (
                    <td key={c.etiqueta} className="px-2 py-1.5 tabular-nums text-gray-700">
                      {c.valor(f)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        children
      )}
    </figure>
  );
}

function Leyenda({ series }) {
  return (
    <div className="flex flex-wrap items-center gap-4 mt-2">
      {series.map((s) => (
        <span key={s.etiqueta} className="inline-flex items-center gap-1.5 text-xs text-gray-600">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />
          {s.etiqueta}
        </span>
      ))}
    </div>
  );
}

// ------------------------------------------------- entradas vs salidas
export function GraficaFlujo({ datos, unidad = 'unidades' }) {
  const [activo, setActivo] = useState(null);
  const id = useId();
  const ancho = 640;
  const alto = 180;
  const margen = { arriba: 8, derecha: 8, abajo: 22, izquierda: 44 };
  const w = ancho - margen.izquierda - margen.derecha;
  const h = alto - margen.arriba - margen.abajo;

  const maximo = Math.max(1, ...datos.flatMap((d) => [Number(d.entradas), Number(d.salidas)]));
  const x = (i) => (datos.length < 2 ? w / 2 : (i / (datos.length - 1)) * w);
  const y = (v) => h - (Number(v) / maximo) * h;
  const linea = (clave) => datos.map((d, i) => `${i ? 'L' : 'M'}${x(i)},${y(d[clave])}`).join(' ');

  const ticks = [0, maximo / 2, maximo];
  const ultimo = datos.length - 1;

  return (
    <Marco
      titulo="Entradas contra salidas"
      ayuda={`Últimos ${datos.length} días, en ${unidad}. Los días sin movimiento valen cero, no se saltan.`}
      tabla={[
        { etiqueta: 'Día', valor: (d) => d.dia },
        { etiqueta: 'Entradas', valor: (d) => Number(d.entradas) },
        { etiqueta: 'Salidas', valor: (d) => Number(d.salidas) },
      ]}
      filas={datos}
    >
      <div className="relative">
        <svg viewBox={`0 0 ${ancho} ${alto}`} className="w-full h-auto" role="img"
             aria-label="Entradas y salidas diarias de los últimos días">
          <g transform={`translate(${margen.izquierda},${margen.arriba})`}>
            {ticks.map((t) => (
              <g key={t}>
                <line x1="0" y1={y(t)} x2={w} y2={y(t)} stroke={REJILLA} strokeWidth="1" />
                <text x="-8" y={y(t)} dy="0.32em" textAnchor="end" fontSize="10"
                      fill={TINTA_EJE} className="tabular-nums">
                  {Math.round(t)}
                </text>
              </g>
            ))}

            <path d={linea('entradas')} fill="none" stroke={SERIE.entradas}
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d={linea('salidas')} fill="none" stroke={SERIE.salidas}
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

            {/* Etiqueta directa solo en el extremo: un número por punto
                sería ilegible y nadie lo lee. */}
            {datos.length > 0 && (
              <>
                <circle cx={x(ultimo)} cy={y(datos[ultimo].entradas)} r="4"
                        fill={SERIE.entradas} stroke="#fff" strokeWidth="2" />
                <circle cx={x(ultimo)} cy={y(datos[ultimo].salidas)} r="4"
                        fill={SERIE.salidas} stroke="#fff" strokeWidth="2" />
              </>
            )}

            {/* Capa de impacto: bandas anchas para que no haya que acertar
                al píxel sobre una línea de 2px. */}
            {datos.map((d, i) => (
              <rect
                key={i}
                x={x(i) - w / datos.length / 2}
                y="0"
                width={w / datos.length}
                height={h}
                fill="transparent"
                onMouseEnter={() => setActivo(i)}
                onMouseLeave={() => setActivo(null)}
              />
            ))}

            {activo != null && (
              <line x1={x(activo)} y1="0" x2={x(activo)} y2={h}
                    stroke={TINTA_EJE} strokeWidth="1" />
            )}

            <text x="0" y={h + 16} fontSize="10" fill={TINTA_EJE}>
              {datos[0]?.dia}
            </text>
            <text x={w} y={h + 16} fontSize="10" fill={TINTA_EJE} textAnchor="end">
              {datos[ultimo]?.dia}
            </text>
          </g>
          <desc id={id}>Serie de entradas y salidas por día.</desc>
        </svg>

        {activo != null && datos[activo] && (
          <div className="absolute top-0 left-0 bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs pointer-events-none"
               style={{ transform: `translateX(${(activo / Math.max(1, datos.length - 1)) * 80}%)` }}>
            <div className="font-semibold text-gray-900">{datos[activo].dia}</div>
            <div className="text-gray-600">Entradas: {Number(datos[activo].entradas)}</div>
            <div className="text-gray-600">Salidas: {Number(datos[activo].salidas)}</div>
          </div>
        )}
      </div>

      <Leyenda series={[
        { etiqueta: 'Entradas', color: SERIE.entradas },
        { etiqueta: 'Salidas', color: SERIE.salidas },
      ]} />
    </Marco>
  );
}

// -------------------------------------------------------- distribución ABC
export function GraficaABC({ datos, formatoValor }) {
  const total = datos.reduce((s, d) => s + Number(d.valor), 0);
  const conValor = datos.filter((d) => Number(d.valor) > 0);

  return (
    <Marco
      titulo="Dónde está el valor (clases ABC)"
      ayuda="Qué proporción del dinero guardado vive en cada clase. La A es la que hay que contar más seguido."
      tabla={[
        { etiqueta: 'Clase', valor: (d) => d.clase },
        { etiqueta: 'Productos', valor: (d) => d.productos },
        { etiqueta: 'Valor', valor: (d) => formatoValor(d.valor) },
      ]}
      filas={datos}
    >
      {total === 0 ? (
        <p className="text-sm text-gray-500 py-6 text-center">
          Sin valor que repartir: da entrada a mercancía con su costo.
        </p>
      ) : (
        <>
          {/* Barra apilada horizontal: son pocas clases con nombres cortos
              y lo que importa es la proporción, no el valor exacto. */}
          <div className="flex w-full h-8 rounded-lg overflow-hidden gap-0.5">
            {conValor.map((d) => {
              const pct = (Number(d.valor) / total) * 100;
              return (
                <div
                  key={d.clase}
                  title={`${d.clase}: ${formatoValor(d.valor)}`}
                  style={{ width: `${pct}%`, background: RAMPA_ABC[d.clase] || RAMPA_ABC.SIN }}
                  className="flex items-center justify-center"
                >
                  {/* La etiqueta solo entra si cabe; si no, vive en la
                      leyenda y en la tabla. */}
                  {pct > 12 && (
                    <span className="text-[11px] font-semibold text-white">
                      {Math.round(pct)}%
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-4 mt-3">
            {datos.map((d) => (
              <span key={d.clase} className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                <span className="w-2.5 h-2.5 rounded-sm shrink-0"
                      style={{ background: RAMPA_ABC[d.clase] || RAMPA_ABC.SIN }} />
                {d.clase === 'SIN' ? 'Sin clasificar' : `Clase ${d.clase}`}
                <span className="text-gray-500">
                  · {d.productos} · {formatoValor(d.valor)}
                </span>
              </span>
            ))}
          </div>
        </>
      )}
    </Marco>
  );
}

// ------------------------------------------------------- mermas por mes
export function GraficaMermas({ datos, formatoValor }) {
  const [activo, setActivo] = useState(null);
  const maximo = Math.max(1, ...datos.map((d) => Number(d.valor)));

  return (
    <Marco
      titulo="Mermas por mes"
      ayuda="Mercancía que salió sin venderse: mermas y ajustes a la baja, valorados a su costo."
      tabla={[
        { etiqueta: 'Mes', valor: (d) => d.mes },
        { etiqueta: 'Unidades', valor: (d) => Number(d.unidades) },
        { etiqueta: 'Valor', valor: (d) => formatoValor(d.valor) },
      ]}
      filas={datos}
    >
      {datos.length === 0 ? (
        <p className="text-sm text-gray-500 py-6 text-center">
          Ninguna merma registrada en los últimos doce meses.
        </p>
      ) : (
        <div className="flex items-end gap-1.5 h-40 pt-2">
          {datos.map((d, i) => {
            const alto = Math.max(2, (Number(d.valor) / maximo) * 130);
            return (
              <div
                key={d.mes}
                className="flex-1 flex flex-col items-center gap-1 min-w-0 relative"
                onMouseEnter={() => setActivo(i)}
                onMouseLeave={() => setActivo(null)}
              >
                {activo === i && (
                  <div className="absolute bottom-full mb-1 bg-white border border-gray-200 rounded-lg shadow-lg px-2.5 py-1.5 text-xs whitespace-nowrap z-10">
                    <div className="font-semibold text-gray-900">{d.mes}</div>
                    <div className="text-gray-600">{formatoValor(d.valor)}</div>
                    <div className="text-gray-600">{Number(d.unidades)} unidades</div>
                  </div>
                )}
                {/* Extremo redondeado de 4px anclado a la base. */}
                <div
                  style={{ height: alto, background: SERIE.entradas }}
                  className="w-full rounded-t"
                />
                <span className="text-[10px] text-gray-500 truncate w-full text-center tabular-nums">
                  {d.mes.slice(5)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Marco>
  );
}
